import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Alert,
} from '@mui/material'
import {
  Inventory2,
  Build,
  Assignment,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
  AccessTime,
  LocalShipping,
  Speed,
  Circle,
  ArrowForward,
} from '@mui/icons-material'
import { Doughnut, Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js'
import { PageHeader, StatCard, Card, LoadingSpinner, StatusChip, PermissionGate } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement)

const Dashboard = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/common/dashboard/')
        setStats(response.data)
      } catch (error) {
        console.error('Error fetching dashboard stats:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />
  }

  const data = stats || {}

  // Chart data for Work Orders by Priority
  const priorityChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.work_orders?.by_priority?.critical || 0,
          data.work_orders?.by_priority?.high || 0,
          data.work_orders?.by_priority?.medium || 0,
          data.work_orders?.by_priority?.low || 0,
        ],
        backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e'],
        borderWidth: 0,
      },
    ],
  }

  // Chart data for Assets by Status
  const assetStatusChartData = {
    labels: ['Operational', 'Under Maintenance', 'Out of Service', 'Retired'],
    datasets: [
      {
        data: [
          data.assets?.by_status?.operational || 0,
          data.assets?.by_status?.under_maintenance || 0,
          data.assets?.by_status?.out_of_service || 0,
          data.assets?.by_status?.retired || 0,
        ],
        backgroundColor: ['#22c55e', '#f97316', '#ef4444', '#6b7280'],
        borderWidth: 0,
      },
    ],
  }

  // Chart data for Assets by Criticality
  const criticalityChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.assets?.by_criticality?.critical || 0,
          data.assets?.by_criticality?.high || 0,
          data.assets?.by_criticality?.medium || 0,
          data.assets?.by_criticality?.low || 0,
        ],
        backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e'],
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 11 },
        },
      },
    },
  }

  // Alerts based on data
  const alerts = []
  if (data.work_orders?.overdue > 0) {
    alerts.push({ severity: 'error', message: `${data.work_orders.overdue} overdue work orders need attention` })
  }
  if (data.preventive_maintenance?.overdue > 0) {
    alerts.push({ severity: 'warning', message: `${data.preventive_maintenance.overdue} PM schedules are overdue` })
  }
  if (data.calibrations?.overdue > 0) {
    alerts.push({ severity: 'warning', message: `${data.calibrations.overdue} calibrations are overdue` })
  }
  if (data.inventory?.out_of_stock > 0) {
    alerts.push({ severity: 'error', message: `${data.inventory.out_of_stock} inventory items are out of stock` })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Box>
      <PageHeader
        title={`Welcome back, ${user?.first_name ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ''}` : user?.username || 'User'}`}
        subtitle="Overview of your maintenance operations"
      />

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Box mb={3} display="flex" flexDirection="column" gap={1}>
          {alerts.map((alert, idx) => (
            <Alert key={idx} severity={alert.severity} variant="outlined">
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Top Stats Row */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Assets"
            value={data.assets?.total || 0}
            icon={<Inventory2 />}
            color="primary"
            onClick={() => navigate('/assets')}
            subtitle={`${data.assets?.operational || 0} operational`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Work Orders"
            value={data.work_orders?.open || 0}
            icon={<Assignment />}
            color="info"
            onClick={() => navigate('/work-orders')}
            subtitle={`${data.work_orders?.created_this_month || 0} this month`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending PM"
            value={data.preventive_maintenance?.pending || 0}
            icon={<Schedule />}
            color="warning"
            onClick={() => navigate('/pm-schedule')}
            subtitle={`${data.preventive_maintenance?.due_this_week || 0} due this week`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Items"
            value={(data.work_orders?.overdue || 0) + (data.preventive_maintenance?.overdue || 0)}
            icon={<Warning />}
            color="error"
            onClick={() => navigate('/work-orders')}
            subtitle="Requires attention"
          />
        </Grid>

        {/* Second Stats Row */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Low Stock Items"
            value={data.inventory?.low_stock || 0}
            icon={<LocalShipping />}
            color="warning"
            onClick={() => navigate('/inventory')}
            subtitle={`${data.inventory?.total_items || 0} total items`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Calibrations Due"
            value={data.calibrations?.due_soon || 0}
            icon={<Speed />}
            color="info"
            onClick={() => navigate('/calibrations')}
            subtitle="Next 30 days"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed This Month"
            value={data.work_orders?.completed_this_month || 0}
            icon={<CheckCircle />}
            color="success"
            subtitle={`${data.preventive_maintenance?.completed_this_month || 0} PM completed`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Resolution Time"
            value={data.work_orders?.avg_resolution_hours ? `${data.work_orders.avg_resolution_hours}h` : '-'}
            icon={<AccessTime />}
            color="primary"
            subtitle="Last 30 days"
          />
        </Grid>

        {/* Charts Row */}
        <Grid item xs={12} md={4}>
          <Card title="Work Orders by Priority">
            <Box height={220}>
              <Doughnut data={priorityChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card title="Assets by Status">
            <Box height={220}>
              <Doughnut data={assetStatusChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card title="Assets by Criticality">
            <Box height={220}>
              <Doughnut data={criticalityChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        {/* Recent Work Orders */}
        <Grid item xs={12} md={8}>
          <Card
            title="Recent Work Orders"
            action={
              <Chip
                label="View All"
                size="small"
                onClick={() => navigate('/work-orders')}
                sx={{ cursor: 'pointer' }}
              />
            }
          >
            {data.work_orders?.recent?.length > 0 ? (
              <List dense disablePadding>
                {data.work_orders.recent.map((wo, index) => (
                  <Box key={wo.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f8fafc' },
                        borderRadius: 1,
                      }}
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Circle
                          sx={{
                            fontSize: 12,
                            color:
                              wo.priority === 'critical'
                                ? '#ef4444'
                                : wo.priority === 'high'
                                ? '#f97316'
                                : wo.priority === 'medium'
                                ? '#eab308'
                                : '#22c55e',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={500}>
                              {wo.work_order_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                              {wo.title}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <StatusChip status={wo.status} size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {wo.asset__name || 'No asset'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(wo.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ArrowForward fontSize="small" color="action" />
                    </ListItem>
                    {index < data.work_orders.recent.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">No recent work orders</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Low Stock Alert */}
          <Card title="Low Stock Items" sx={{ mb: 3 }}>
            {data.inventory?.low_stock_list?.length > 0 ? (
              <List dense disablePadding>
                {data.inventory.low_stock_list.map((item, index) => (
                  <Box key={item.id}>
                    <ListItem
                      sx={{ px: 0, cursor: 'pointer' }}
                      onClick={() => navigate('/inventory')}
                    >
                      <ListItemText
                        primary={item.name}
                        secondary={`Part #: ${item.part_number}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={item.current_stock === 0 ? 'error.main' : 'warning.main'}
                        >
                          {item.current_stock} / {item.reorder_point}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((item.current_stock / item.reorder_point) * 100, 100)}
                          color={item.current_stock === 0 ? 'error' : 'warning'}
                          sx={{ width: 60, height: 4, borderRadius: 2, mt: 0.5 }}
                        />
                      </Box>
                    </ListItem>
                    {index < data.inventory.low_stock_list.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={2} textAlign="center">
                <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  All stock levels are healthy
                </Typography>
              </Box>
            )}
          </Card>

          {/* Quick Actions */}
          <Card title="Quick Actions">
            <Box display="flex" flexDirection="column" gap={2}>
              <PermissionGate permission="work_orders.create">
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f1f5f9' },
                  }}
                  onClick={() => navigate('/work-orders')}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Build color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Create Work Order
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Log a new maintenance request
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>

              <PermissionGate permission="assets.create">
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f1f5f9' },
                  }}
                  onClick={() => navigate('/assets')}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Inventory2 color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Register Asset
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add a new asset to the system
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>

              <PermissionGate permission="reports.view">
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f1f5f9' },
                  }}
                  onClick={() => navigate('/reports')}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <TrendingUp color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        View Reports
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Analytics and insights
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>
            </Box>
          </Card>
        </Grid>

        {/* Assets by Category */}
        <Grid item xs={12} md={6}>
          <Card title="Top Asset Categories">
            {data.assets?.by_category?.length > 0 ? (
              <List dense disablePadding>
                {data.assets.by_category.map((cat, index) => (
                  <Box key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={cat.category__name || 'Uncategorized'}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinearProgress
                          variant="determinate"
                          value={(cat.count / data.assets.total) * 100}
                          sx={{ width: 100, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
                          {cat.count}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < data.assets.by_category.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={2} textAlign="center">
                <Typography color="text.secondary">No category data</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Summary Stats */}
        <Grid item xs={12} md={6}>
          <Card title="Monthly Summary">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center" py={2}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {data.work_orders?.created_this_month || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Work Orders Created
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" py={2}>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {data.work_orders?.completed_this_month || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Work Orders Completed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" py={2}>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {data.preventive_maintenance?.completed_this_month || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PM Completed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" py={2}>
                  <Typography variant="h4" fontWeight={700} color="secondary.main">
                    {data.vendors?.active || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Vendors
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard
